import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaFileAlt, FaFilter } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import {
  listarGruposContasPlanoContas,
  baixarPdfPlanoContasContas,
} from '~/Services/Service';

const TODOS_GRUPOS = { value: -1, label: 'TODOS' };

const RelatorioPlanoContas = () => {
  const [grupos, setGrupos] = useState([]);
  const [carregandoGrupos, setCarregandoGrupos] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [gerandoPdf, setGerandoPdf] = useState(false);

  useEffect(() => {
    let ativo = true;

    const carregarGrupos = async () => {
      setCarregandoGrupos(true);
      try {
        const res = await listarGruposContasPlanoContas();
        if (!ativo) return;
        setGrupos(Array.isArray(res) ? res : []);
      } catch (err) {
        if (!ativo) return;
        const mensagem =
          err && err.message ? err.message : 'Erro ao carregar grupos de contas.';
        toast.error(mensagem);
      } finally {
        if (ativo) {
          setCarregandoGrupos(false);
        }
      }
    };

    carregarGrupos();

    return () => {
      ativo = false;
    };
  }, []);

  // Limpa o objectURL ao desmontar pra evitar leak.
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const opcoesGrupos = useMemo(() => {
    const opcoes = grupos.map((grupo) => ({
      value: grupo.codigo,
      label: grupo.descricao,
    }));

    return [TODOS_GRUPOS, ...opcoes];
  }, [grupos]);

  const handleSubmit = async (values) => {
    setGerandoPdf(true);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
    try {
      const blob = await baixarPdfPlanoContasContas({
        grupo: values.grupo,
        expandido: values.expandido,
      });
      const novaUrl = window.URL.createObjectURL(
        new Blob([blob], { type: 'application/pdf' })
      );
      setPdfUrl(novaUrl);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao gerar o relatório.';
      toast.error(mensagem);
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Plano de Contas - Contas" />

      <Card className="mt-3">
        <Card.Body>
          <Formik
            initialValues={{ grupo: TODOS_GRUPOS.value, expandido: false }}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, handleSubmit: submit }) => (
              <form onSubmit={submit}>
                <Row className="align-items-end">
                  <Col xs={12} md={6} lg={5}>
                    <Form.Group>
                      <Form.Label>Categoria Contas</Form.Label>
                      <Select
                        options={opcoesGrupos}
                        value={
                          opcoesGrupos.find((opcao) => opcao.value === values.grupo) ||
                          TODOS_GRUPOS
                        }
                        onChange={(opcao) =>
                          setFieldValue(
                            'grupo',
                            opcao ? opcao.value : TODOS_GRUPOS.value
                          )
                        }
                        isClearable={false}
                        isLoading={carregandoGrupos}
                        styles={selectStylesSm}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={3} lg={2}>
                    <Form.Group>
                      <Form.Check
                        id="relatorio-plano-contas-expandido"
                        type="checkbox"
                        label="Expandido"
                        checked={values.expandido}
                        onChange={(event) =>
                          setFieldValue('expandido', event.target.checked)
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md="auto">
                    <Form.Group>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={carregandoGrupos || gerandoPdf}
                      >
                        <FaFilter className="mr-1" />
                        Gerar Relatório
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </form>
            )}
          </Formik>
        </Card.Body>
      </Card>

      {(carregandoGrupos || gerandoPdf) && (
        <Loading label={gerandoPdf ? 'Gerando relatório...' : 'Carregando grupos de contas...'} />
      )}

      <Card className="mt-3">
        <Card.Body className="p-0">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Relatório Plano de Contas"
              style={{ width: '100%', minHeight: '72vh', border: 0 }}
            />
          ) : (
            <div className="text-muted text-center py-5">
              <FaFileAlt className="mr-2" />
              Informe os filtros para gerar o relatório.
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RelatorioPlanoContas;
